import type { SelectedTimePeriod } from "replo-utils/analytics";
import type {
  AnalyticsQuery,
  MetricName,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { MetricsOverview } from "@editor/components/analytics/home/MetricsOverview";
import { TopPagesTable } from "@editor/components/analytics/home/TopPagesTable";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { formatValueToTableCell } from "@editor/utils/analytics";
import { routes } from "@editor/utils/router";

import { InsightsCard } from "@/features/analytics/insights/InsightsCard";
import useAnalyticsInsights from "@/features/analytics/useAnalyticsInsights";
import useWorkspaceUrlHosts from "@/features/analytics/useWorkspaceUrlHosts";
import Button from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { AppWindow, Sparkles } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { getRangesFromTimePeriod } from "replo-utils/analytics";
import { PageTypeEnum } from "schemas/generated/analyticsRead";

import { DEFAULT_METRICS } from "./constants";
import { useAnalyticsQueryContext } from "./contexts/AnalyticsQueryContext";
import { ReploLineChart } from "./ReploLineChart";
import { AnalyticsMainRangeCombobox } from "./selects/AnalyticsTimeRangeCombobox";
import useBasicAnalyticsRead from "./useBasicAnalyticsRead";

const Card: React.FC<
  React.PropsWithChildren<{
    title: string;
    icon: React.ReactNode;
    path: string;
  }>
> = ({ children, title, icon, path }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-2 rounded-lg">
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-base font-semibold">{title}</span>
        </div>
        <Button variant="secondary" onClick={() => navigate(path)}>
          See all
        </Button>
      </div>
      {children}
    </div>
  );
};

const SELECTED_TIME_PERIOD: SelectedTimePeriod = {
  type: "relative",
  value: "last-week",
};

export const AnalyticsHome: React.FC = () => {
  const { workspace } = useCurrentWorkspaceContext();
  const workspaceId = workspace?.id ?? "";
  const workspaceCurrency = workspace?.currency ?? "USD";

  const workspaceUrlHosts = useWorkspaceUrlHosts(workspaceId);

  const { query, timeZone } = useAnalyticsQueryContext();

  const [activeMetric, setActiveMetric] = React.useState<MetricName>("revenue");

  const { allInsights, activeGroupInsights } =
    useAnalyticsInsights("build-in-replo");

  const handleMetricChange = (metric: MetricName) => {
    setActiveMetric(metric);
  };

  const insightToShow = activeGroupInsights?.[0] ?? allInsights?.[0];

  const ranges = getRangesFromTimePeriod({
    selectedTimePeriod: query.selectedTimePeriod,
    chartInterval: null,
    timeZone,
  }).updatedRanges;

  const pageQueryConfig: AnalyticsQuery = {
    ranges,
    sortMetric: "unique_sessions",
    order: "DESC",
    limit: 3,
    offset: 0,
    filters: {
      urlPath: [],
      pageType: PageTypeEnum.ENTRY_PAGES,
      urlParams: {},
    },
    group: "urlPath",
    urlHosts: workspaceUrlHosts.data,
    metrics: DEFAULT_METRICS,
    isFractional: false,
  };

  const { mainRangeResults: mainPageResults, isLoading: isPageResultsLoading } =
    useBasicAnalyticsRead({
      workspaceId,
      type: "withoutUrlSyncQuery",
      queryConfig: pageQueryConfig,
    });

  const {
    mainRangeResults: mainDomainResults,
    compareAtRangeResults: compareDomainResults,
    isLoading: isDomainResultsLoading,
  } = useBasicAnalyticsRead({
    workspaceId,
    type: "withoutUrlSyncQuery",
    queryConfig: { ...pageQueryConfig, group: "domain" },
  });

  const chartRanges = getRangesFromTimePeriod({
    selectedTimePeriod: SELECTED_TIME_PERIOD,
    chartInterval: "day",
    timeZone: "UTC",
  }).updatedRanges;

  const {
    mainRangeResults: mainDomainChartResults,
    compareAtRangeResults: compareDomainChartResults,
    isLoading: isDomainChartResultsLoading,
  } = useBasicAnalyticsRead({
    workspaceId,
    type: "withoutUrlSyncQuery",
    queryConfig: {
      ...pageQueryConfig,
      group: "domain",
      ranges: chartRanges,
    },
  });

  const cards = [
    {
      id: "insights",
      title: "Insights",
      icon: <Sparkles size={18} />,
      path: generatePath(routes.analytics.insights, {
        workspaceId,
      }),
      children: insightToShow && <InsightsCard insight={insightToShow} />,
    },
    {
      id: "top-pages",
      title: "Top Pages",
      icon: <AppWindow size={18} />,
      path: generatePath(routes.analytics.overview, {
        workspaceId,
      }),
      children: (
        <TopPagesTable
          isLoading={isPageResultsLoading}
          topPages={mainPageResults}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="flex">
        <AnalyticsMainRangeCombobox />
      </div>
      <div className="flex flex-col gap-2">
        <MetricsOverview
          mainRangeResults={mainDomainResults}
          compareAtRangeResults={compareDomainResults}
          isLoading={isDomainResultsLoading}
          handleMetricChange={handleMetricChange}
          activeMetric={activeMetric}
        />
        {isDomainChartResultsLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <div className="border border-secondary p-4 rounded-lg">
            <ReploLineChart
              thisPeriod={
                mainDomainChartResults[0]?.metrics[activeMetric] ?? []
              }
              previousPeriod={
                compareDomainChartResults[0]?.metrics[activeMetric] ?? []
              }
              ranges={chartRanges}
              chartInterval="day"
              timeZone="UTC"
              className="h-[250px]"
              tooltipValueFormatter={(value) =>
                formatValueToTableCell({
                  name: activeMetric,
                  value,
                  currency: workspaceCurrency,
                  isFractional: query.isFractional,
                })
              }
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {cards.map((card) => (
          <Card
            key={card.id}
            title={card.title}
            icon={card.icon}
            path={card.path}
          >
            {card.children}
          </Card>
        ))}
      </div>
    </div>
  );
};
